import React, { useState } from 'react'
import QRCode from 'react-qr-code'
import { useLoaderData, useNavigate } from 'react-router-dom'
import { t } from '@lingui/core/macro'
import { usePaymentResultRealtime } from '../../hooks/usePaymentResultRealtime'
import { PixPayPageData } from '../../loaders/pixPayPageLoader'
import Expired from '../../components/Expired/Expired'
import TimeLeft from '../../components/TimeLeft/TimeLeft'
import OrderInfo from '../../components/OrderInfo/OrderInfo'
import CopyButton from '../../components/CopyButton/CopyButton'
import styles from './PixPage.module.css'

type PaymentTab = 'qr' | 'text'

const PixPage = () => {
    const { paymentPageInfo, transaction } = useLoaderData<PixPayPageData>()

    const navigate = useNavigate()

    const [isExpired, setIsExpired] = useState(paymentPageInfo.expiresAt && new Date(paymentPageInfo.expiresAt) < new Date())
    const [paymentTab, setPaymentTab] = useState<PaymentTab>('qr')

    const { paymentResult, loading } = usePaymentResultRealtime(
        paymentPageInfo.id,
        transaction.transactionId,
    )

    if (!loading && paymentResult) {
        navigate(`/result/${paymentPageInfo.id}/${transaction.transactionId}`)
    }

    if (isExpired) {
        return (
            <Expired
                orderId={paymentPageInfo.orderId}
                redirectUrl={paymentPageInfo.redirectUrl}
                redirectTimeout={paymentPageInfo.redirectTimeout}
            />
        )
    }

    return (
        <>
            <TimeLeft
                expiresAt={paymentPageInfo.expiresAt}
                onExpired={() => setIsExpired(true)}
            />
            <div className={styles.container}>
                <h2 className={styles.title}>{t`Оплата через Pix`}</h2>

                <p className={styles.text}>
                    {t`Быстрая и безопасная оплата через бразильскую систему Pix`}
                </p>

                <OrderInfo transaction={transaction} orderId={paymentPageInfo.orderId} description={paymentPageInfo.description} />

                <div className={styles.tabs}>
                    <button
                        className={`${styles.tab} ${paymentTab === 'qr' ? styles.tabActive : ''}`}
                        onClick={() => setPaymentTab('qr')}
                    >
                        {t`QR-код`}
                    </button>
                    <button
                        className={`${styles.tab} ${paymentTab === 'text' ? styles.tabActive : ''}`}
                        onClick={() => setPaymentTab('text')}
                    >
                        Pix Copia e Cola
                    </button>
                </div>

                <div className={styles.content}>
                    {paymentTab === 'qr' ? (
                        <div className={styles.qrSection}>
                            <div className={styles.qrCode}>
                                <QRCode
                                    size={256}
                                    style={{ height: "auto", maxWidth: "100%", width: "100%" }}
                                    value={transaction.qrCodeData}
                                    viewBox={`0 0 256 256`}
                                />
                            </div>

                            <div className={styles.instruction}>
                                <h3 className={styles.instructionTitle}>{t`Как оплатить:`}</h3>
                                <ol className={styles.instructionList}>
                                    <li>{t`Откройте приложение вашего банка`}</li>
                                    <li>{t`Выберите "Pix" и "Оплатить QR-код"`}</li>
                                    <li>{t`Наведите камеру на QR-код выше`}</li>
                                    <li>{t`Подтвердите платеж в приложении`}</li>
                                </ol>
                                {transaction.qrCodeLink && (
                                    <div className={styles.linkSection}>
                                        <a href={transaction.qrCodeLink}
                                           target="_blank"
                                           rel="noopener noreferrer"
                                           className={styles.pixLink}
                                        >
                                            {t`Открыть на сайте Pix →`}
                                        </a>
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : (
                        <div className={styles.codeSection}>
                            <p className={styles.codeSectionText}>
                                {t`Скопируйте код и вставьте его в приложении банка`}
                            </p>
                            <div className={styles.codeBox}>
                                <code className={styles.code}>{transaction.qrCodeData}</code>
                            </div>
                            <CopyButton text={transaction.qrCodeData} />

                            <div className={styles.instruction}>
                                <h3 className={styles.instructionTitle}>{t`Как оплатить:`}</h3>
                                <ol className={styles.instructionList}>
                                    <li>{t`Нажмите кнопку "Скопировать код" выше`}</li>
                                    <li>{t`Откройте приложение вашего банка`}</li>
                                    <li>{t`Выберите "Pix" → "Pix Copia e Cola"`}</li>
                                    <li>{t`Вставьте скопированный код`}</li>
                                    <li>{t`Подтвердите оплату`}</li>
                                </ol>
                                {transaction.qrCodeLink && (
                                    <div className={styles.linkSection}>
                                        <a href={transaction.qrCodeLink}
                                           target="_blank"
                                           rel="noopener noreferrer"
                                           className={styles.pixLink}
                                        >
                                            {t`Открыть на сайте Pix →`}
                                        </a>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </>
    )
}

export default PixPage
